/**
 * LogbookModal — screen-snapshot memo creator.
 *
 * Screenshot is captured on-demand ("Take Screenshot" button), NOT on modal open.
 * Additional images can be picked from the device gallery.
 * All images are saved as proper binary attachments (imageCount/imagePaths) so
 * MemosPage renders them inline — NOT as text paths.
 *
 * Text and screenshot state are persisted by the parent (App.tsx) across
 * open/close cycles. State is cleared after a successful send or save.
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { useMemoRecorder } from '../hooks/useMemoRecorder';
import { authFetch } from '../api/authFetch';
import { useSettingsStore } from '../state/settings.store';

interface Props {
  onClose: () => void;
  /** saveMemo from useMemos hook — used for voice path */
  onSaveMemo: (chunks: Blob[], durationMs: number, language?: string) => void;
  /** saveTextMemo from useMemos hook — used for text+image path */
  onSaveTextMemo: (text: string, images?: { data: string; mimeType: string; fileName: string }[]) => Promise<void>;
  /** Whether the memos feature is enabled for this profile */
  memosEnabled: boolean;
  /** Send the logbook note + screenshot path directly to the active agent chat session */
  onExecute: (text: string) => void;
  /** Persisted text from parent — survives modal close/reopen */
  persistedText: string;
  /** Update persisted text in parent */
  onTextChange: (text: string) => void;
  /** Persisted screenshot data URL from parent — survives modal close/reopen */
  persistedScreenshot: string | null;
  /** Update persisted screenshot in parent */
  onScreenshotChange: (dataUrl: string | null) => void;
  /** Clear all persisted logbook state (after successful send/save) */
  onClearState: () => void;
}

interface GalleryImage {
  dataUrl: string;
  mimeType: string;
  fileName: string;
}

const STT_LANGS = [{ code: 'en', label: 'EN' }, { code: 'bg', label: 'BG' }, { code: '', label: 'Auto' }];

/** Convert a data URL to base64 string (strips the prefix). */
function dataUrlToBase64(dataUrl: string): string {
  return dataUrl.split(',')[1] ?? '';
}

/** Convert a data URL to a mime type string. */
function dataUrlMime(dataUrl: string): string {
  return dataUrl.match(/data:([^;]+)/)?.[1] ?? 'image/jpeg';
}

export function LogbookModal({
  onClose, onSaveMemo, onSaveTextMemo, memosEnabled, onExecute,
  persistedText, onTextChange, persistedScreenshot, onScreenshotChange, onClearState,
}: Props) {
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [screenshotError, setScreenshotError] = useState(false);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [executed, setExecuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'text' | 'voice'>('text');
  // settings-zustand-migration Phase 2: legacy 'stt-language' migrated into the store.
  const sttLanguage = useSettingsStore((s) => s.sttLanguage);
  const [recordedChunks, setRecordedChunks] = useState<{ chunks: Blob[]; durationMs: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { recording, durationMs, micError, start: startRec, stop: stopRec } = useMemoRecorder();

  /** Upload screenshot and return the server path (for bug reports sent to agent). */
  const uploadScreenshotForAgent = useCallback(async (dataUrl: string): Promise<string | null> => {
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const form = new FormData();
      form.append('file', blob, 'screenshot.jpg');
      const uploadRes = await authFetch('/api/screenshots', { method: 'POST', body: form });
      if (!uploadRes.ok) throw new Error(await uploadRes.text());
      const { path } = await uploadRes.json() as { path: string; retentionDays: number };
      return path;
    } catch {
      return null;
    }
  }, []);

  const captureScreenshot = useCallback(async () => {
    setScreenshotLoading(true);
    setScreenshotError(false);

    // Hide the logbook overlay so it doesn't appear in the screenshot
    const overlay = modalRef.current?.parentElement as HTMLElement | null;
    if (overlay) overlay.style.display = 'none';
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

    try {
      const { toJpeg } = await import('html-to-image');
      const dataUrl = await toJpeg(document.body, {
        quality: 0.7,
        pixelRatio: 0.5,
      });
      onScreenshotChange(dataUrl);
    } catch {
      setScreenshotError(true);
    } finally {
      if (overlay) overlay.style.display = '';
      setScreenshotLoading(false);
    }
  }, [onScreenshotChange]);

  /** Open device gallery file picker. */
  const openGallery = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /** Handle files selected from gallery. */
  const handleGalleryFiles = useCallback((e: Event) => {
    const files = Array.from((e.target as HTMLInputElement).files ?? []);
    if (!files.length) return;

    const readers = files.map((file) => new Promise<GalleryImage>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        dataUrl: reader.result as string,
        mimeType: file.type || 'image/jpeg',
        fileName: file.name,
      });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }));

    Promise.all(readers).then((imgs) => {
      setGalleryImages((prev) => [...prev, ...imgs]);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }).catch(() => setError('Failed to read selected images'));
  }, []);

  const removeGalleryImage = useCallback((idx: number) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  /** Build images array for memo save — all screenshots + gallery images as base64 attachments. */
  const buildImageAttachments = useCallback((): { data: string; mimeType: string; fileName: string }[] => {
    const imgs: { data: string; mimeType: string; fileName: string }[] = [];
    if (persistedScreenshot) {
      imgs.push({
        data: dataUrlToBase64(persistedScreenshot),
        mimeType: dataUrlMime(persistedScreenshot),
        fileName: 'screenshot.jpg',
      });
    }
    for (const g of galleryImages) {
      imgs.push({
        data: dataUrlToBase64(g.dataUrl),
        mimeType: g.mimeType,
        fileName: g.fileName,
      });
    }
    return imgs;
  }, [persistedScreenshot, galleryImages]);

  useEffect(() => {
    if (mode === 'text') setTimeout(() => textareaRef.current?.focus(), 50);
  }, [mode]);

  /** Save text bug to archive + optionally send to session. */
  const handleTextBug = useCallback(async (intent: 'fix' | 'plan') => {
    const note = persistedText.trim();
    if (!persistedScreenshot && !note) {
      setError('Add a note or take a screenshot first.');
      return;
    }

    setUploading(true);
    setError(null);

    let screenshotPath: string | null = null;
    if (persistedScreenshot) {
      screenshotPath = await uploadScreenshotForAgent(persistedScreenshot);
    }

    const text = note || '(no description)';
    const images = buildImageAttachments();

    try {
      await authFetch('/api/memos/bugs/from-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, intent, images: images.length ? images : undefined }),
      });
    } catch {
      // Non-fatal — bug archival failure shouldn't block Fix now
    }

    if (intent === 'fix') {
      const header = note ? `[Bug Report] ${note}` : '[Bug Report] (no description)';
      const msg = screenshotPath ? `${header}\nScreenshot: ${screenshotPath}` : header;
      onExecute(msg);
    }

    setUploading(false);
    setExecuted(true);
    onClearState();
    setGalleryImages([]);
    setTimeout(onClose, 700);
  }, [persistedText, persistedScreenshot, uploadScreenshotForAgent, buildImageAttachments, onExecute, onClearState, onClose]);

  /** Save as memo with images as proper binary attachments (renders inline in MemosPage). */
  const submitText = useCallback(async () => {
    const note = persistedText.trim();
    if (!note) { setError('Add a note before saving.'); return; }
    setSaving(true);
    setError(null);

    try {
      const images = buildImageAttachments();
      await onSaveTextMemo(`[Logbook] ${note}`, images.length ? images : undefined);
      setSaved(true);
      onClearState();
      setGalleryImages([]);
      setTimeout(onClose, 900);
    } catch (e: any) {
      setError(e.message || 'Failed to save memo');
      setSaving(false);
    }
  }, [persistedText, buildImageAttachments, onSaveTextMemo, onClearState, onClose]);

  const handleVoiceToggle = useCallback(async () => {
    if (recording) {
      const { chunks, durationMs: dur } = await stopRec();
      if (chunks.length) {
        setRecordedChunks({ chunks, durationMs: dur });
      }
    } else {
      setRecordedChunks(null);
      await startRec();
    }
  }, [recording, startRec, stopRec]);

  const handleSaveVoiceMemo = useCallback(async () => {
    if (!recordedChunks) return;
    onSaveMemo(recordedChunks.chunks, recordedChunks.durationMs, sttLanguage || undefined);
    setSaved(true);
    setRecordedChunks(null);
    onClearState();
    setGalleryImages([]);
    setTimeout(onClose, 900);
  }, [recordedChunks, onSaveMemo, sttLanguage, onClearState, onClose]);

  const handleDiscardRecording = useCallback(() => {
    setRecordedChunks(null);
  }, []);

  /** Upload voice bug audio to archive. Transcript arrives via BUG_TRANSCRIBED socket event. */
  const handleVoiceBug = useCallback(async (intent: 'fix' | 'plan') => {
    if (!recordedChunks) return;
    setUploading(true);
    setError(null);

    try {
      const blob = new Blob(recordedChunks.chunks, { type: 'audio/webm' });
      const arrayBuf = await blob.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuf)));
      await authFetch('/api/memos/bugs/from-audio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audio: base64, intent, durationMs: recordedChunks.durationMs }),
      });
    } catch {
      setError('Failed to upload bug recording.');
      setUploading(false);
      return;
    }

    setUploading(false);
    setExecuted(true);
    setRecordedChunks(null);
    onClearState();
    setGalleryImages([]);
    setTimeout(onClose, 900);
  }, [recordedChunks, onClearState, onClose]);

  const formatDuration = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const totalImages = (persistedScreenshot ? 1 : 0) + galleryImages.length;
  const canSend = persistedScreenshot || galleryImages.length > 0 || persistedText.trim().length > 0;

  return (
    <div class="logbook-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Hidden gallery file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style="display:none"
        onChange={handleGalleryFiles}
      />

      <div class="logbook-modal" ref={modalRef}>
        {/* Header */}
        <div class="logbook-header">
          <span class="logbook-title">🐜 Logbook</span>
          <button class="logbook-close-btn" onClick={onClose} type="button">✕</button>
        </div>

        {/* Screenshot section */}
        <div class="logbook-screenshot-section">
          {screenshotLoading ? (
            <div class="logbook-skeleton-bar" />
          ) : persistedScreenshot ? (
            <div class="logbook-screenshot-wrap">
              <img class="logbook-screenshot" src={persistedScreenshot} alt="Screen snapshot" />
              <button
                class="logbook-screenshot-detach"
                type="button"
                title="Remove screenshot"
                onClick={() => onScreenshotChange(null)}
              >✕</button>
            </div>
          ) : (
            <div class="logbook-screenshot-unavailable">
              {screenshotError ? '📷 Capture failed — try again' : '📷 No screenshot yet'}
            </div>
          )}

          {/* Gallery images strip */}
          {galleryImages.length > 0 && (
            <div class="logbook-gallery-strip">
              {galleryImages.map((img, i) => (
                <div key={i} class="logbook-gallery-thumb-wrap">
                  <img class="logbook-gallery-thumb" src={img.dataUrl} alt={img.fileName} />
                  <button
                    class="logbook-screenshot-detach"
                    type="button"
                    title="Remove image"
                    onClick={() => removeGalleryImage(i)}
                  >✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Capture + gallery buttons row */}
          <div class="logbook-capture-row">
            <button
              class="logbook-retake-btn"
              onClick={captureScreenshot}
              disabled={screenshotLoading}
              type="button"
            >
              {screenshotLoading ? '…' : persistedScreenshot ? '📷 Retake' : '📷 Take Screenshot'}
            </button>
            <button
              class="logbook-gallery-btn"
              onClick={openGallery}
              type="button"
              title="Attach images from gallery"
            >
              🖼 {totalImages > 0 ? `${totalImages} attached` : 'Attach Images'}
            </button>
          </div>
        </div>

        {/* Mode toggle */}
        <div class="logbook-mode-tabs">
          <button
            class={`logbook-mode-tab${mode === 'text' ? ' active' : ''}`}
            onClick={() => setMode('text')}
            type="button"
          >✏️ Text</button>
          {memosEnabled && (
            <button
              class={`logbook-mode-tab${mode === 'voice' ? ' active' : ''}`}
              onClick={() => setMode('voice')}
              type="button"
            >🎙 Voice</button>
          )}
        </div>

        {/* Text input */}
        {mode === 'text' && (
          <div class="logbook-text-area-wrap">
            <textarea
              ref={textareaRef}
              class="logbook-textarea"
              placeholder="Describe what you're seeing… (optional when screenshot attached)"
              value={persistedText}
              onInput={(e) => onTextChange((e.target as HTMLTextAreaElement).value)}
              rows={3}
            />
          </div>
        )}

        {/* Voice input */}
        {mode === 'voice' && (
          <div class="logbook-voice-wrap">
            {recordedChunks ? (
              <div class="logbook-voice-captured">
                🎙 Captured: {formatDuration(recordedChunks.durationMs)} — save or discard below
              </div>
            ) : (
              <>
                {!recording && (
                  <div class="stt-lang-row">
                    {STT_LANGS.map((l) => (
                      <button
                        key={l.code}
                        class={`stt-lang-btn${sttLanguage === l.code ? ' active' : ''}`}
                        onClick={() => useSettingsStore.getState().setSttLanguage(l.code)}
                        type="button"
                        title={l.code === '' ? 'Auto-detect language' : `Transcribe in ${l.label}`}
                      >{l.label}</button>
                    ))}
                  </div>
                )}
                <button
                  class={`logbook-voice-btn${recording ? ' recording' : ''}`}
                  onClick={handleVoiceToggle}
                  type="button"
                >
                  {recording ? `⏹ ${formatDuration(durationMs)}` : '🎙 Hold to record'}
                </button>
                {micError && <div class="logbook-error">{micError}</div>}
              </>
            )}
          </div>
        )}

        {error && <div class="logbook-error">{error}</div>}
        {saved && <div class="logbook-saved">Saved to memos!</div>}
        {executed && <div class="logbook-saved">Bug logged!</div>}

        {!saved && !executed && (
          <div class="logbook-footer">
            <button
              class="logbook-cancel-btn"
              onClick={recordedChunks ? handleDiscardRecording : onClose}
              type="button"
            >
              {recordedChunks ? 'Discard' : 'Cancel'}
            </button>
            {mode === 'text' && (
              <>
                <button
                  class="logbook-execute-btn"
                  onClick={() => handleTextBug('fix')}
                  disabled={!canSend || uploading}
                  type="button"
                  title="Save bug + send to session now"
                >
                  {uploading ? 'Uploading…' : '🔧 Fix now'}
                </button>
                <button
                  class="logbook-save-btn logbook-plan-btn"
                  onClick={() => handleTextBug('plan')}
                  disabled={!canSend || uploading}
                  type="button"
                  title="Save bug + add to planning pipeline"
                >
                  📌 Plan it
                </button>
                {memosEnabled && (
                  <button
                    class="logbook-save-btn"
                    onClick={submitText}
                    disabled={saving || !persistedText.trim()}
                    type="button"
                  >
                    {saving ? 'Saving…' : 'Save Memo'}
                  </button>
                )}
              </>
            )}
            {mode === 'voice' && recordedChunks && (
              <>
                <button
                  class="logbook-execute-btn"
                  onClick={() => handleVoiceBug('fix')}
                  disabled={uploading}
                  type="button"
                  title="Save bug — transcript arrives in session automatically"
                >
                  {uploading ? 'Uploading…' : '🔧 Fix now'}
                </button>
                <button
                  class="logbook-save-btn logbook-plan-btn"
                  onClick={() => handleVoiceBug('plan')}
                  disabled={uploading}
                  type="button"
                  title="Save bug + add to planning pipeline"
                >
                  📌 Plan it
                </button>
                {memosEnabled && (
                  <button
                    class="logbook-save-btn"
                    onClick={handleSaveVoiceMemo}
                    type="button"
                  >
                    Save Memo
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
